/**
 * Criptografía de cliente para la sincronización cifrada de la config IA.
 *
 * Modelo Bitwarden-style: la API key del proveedor nunca sale del
 * navegador en claro. Se cifra con AES-GCM usando una clave derivada por
 * PBKDF2 de la contraseña del usuario + un salt por usuario. Supabase sólo
 * almacena ciphertext + iv + salt; ni el operador ni Supabase pueden
 * descifrar sin la contraseña.
 *
 * Requiere contexto seguro (https o localhost), ya asumido por la PWA
 * (el service worker no se registra fuera de contexto seguro y
 * `crypto.subtle` no existe fuera de él — ver utils/hash.ts).
 */

/** Iteraciones PBKDF2. ~300 ms en hardware moderno; balance seguridad/UX. */
const PBKDF2_ITERATIONS = 250_000
const SALT_BYTES = 16
const IV_BYTES = 12

export type SealedPayload = {
  /** Ciphertext AES-GCM en base64. */
  ciphertext: string
  /** Vector de inicialización (12 bytes) en base64. */
  iv: string
  /** Salt PBKDF2 (16 bytes) en base64. */
  salt: string
}

/* ----------------------------- bytes <-> base64 ----------------------------- */

export function toBase64(bytes: Uint8Array): string {
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

export function fromBase64(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/* --------------------------------- salts ---------------------------------- */

/** Salt aleatorio de 16 bytes para PBKDF2 (único por usuario). */
export function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(SALT_BYTES))
}

/* ------------------------------ derivación -------------------------------- */

/**
 * Deriva una `CryptoKey` AES-GCM (encrypt+decrypt) a partir de la
 * contraseña del usuario y un salt. Costoso a propósito (~300 ms).
 *
 * `extractable` (default false) permite exportar los bytes de la clave.
 * Se usa SOLO para guardarla en sessionStorage tras un desbloqueo (en vez
 * de la contraseña en claro): quien robe esa clave sólo puede descifrar la
 * config IA de este usuario, no reutilizar su contraseña de cuenta.
 */
export async function deriveKey(
  password: string,
  salt: Uint8Array,
  extractable = false,
): Promise<CryptoKey> {
  const enc = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, [
    'deriveKey',
  ])
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    extractable,
    ['encrypt', 'decrypt'],
  )
}

/** Importa una `CryptoKey` AES-GCM desde sus bytes crudos (32 bytes). */
export async function importKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ])
}

/* ------------------------------ cifrado ----------------------------------- */

/** Cifra `plaintext` con AES-GCM. Devuelve ciphertext + iv + salt(base64). */
export async function encrypt(
  key: CryptoKey,
  plaintext: string,
  salt: Uint8Array,
): Promise<SealedPayload> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
  const enc = new TextEncoder()
  const ctBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plaintext))
  return {
    ciphertext: toBase64(new Uint8Array(ctBuf)),
    iv: toBase64(iv),
    salt: toBase64(salt),
  }
}

/** Descifra un `SealedPayload` con la clave derivada. Lanza si la clave es incorrecta. */
export async function decrypt(key: CryptoKey, payload: SealedPayload): Promise<string> {
  const iv = fromBase64(payload.iv)
  const ct = fromBase64(payload.ciphertext)
  const ptBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct)
  return new TextDecoder().decode(ptBuf)
}
